import React, { useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';

interface OptionData {
  option_text_en: string;
  option_text_fr?: string;
  option_image_en?: string;
  option_image_fr?: string;
  is_correct: boolean;
  points?: number;
}

interface PreviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  questionType: string;
  questionText: string;
  questionImage?: string;
  options: OptionData[];
  language?: 'en' | 'fr';
}

const PreviewModal: React.FC<PreviewModalProps> = ({
  open,
  onOpenChange,
  questionType,
  questionText,
  questionImage,
  options,
  language = 'en',
}) => {
  // Calculate total points for the question
  const totalPoints = useMemo(() => {
    return options.reduce((sum, opt) => sum + (opt.points || 0), 0);
  }, [options]);

  // Mock team data for team/player questions
  const mockTeams = useMemo(() => {
    if (questionType === 'team' && options.length >= 2) {
      return [
        {
          id: 'team-1',
          name: options[0][language === 'en' ? 'option_text_en' : 'option_text_fr'],
          color: '#c8102e',
          accent: '#fff',
          jerseyUrl: options[0][language === 'en' ? 'option_image_en' : 'option_image_fr'],
        },
        {
          id: 'team-2',
          name: options[1][language === 'en' ? 'option_text_en' : 'option_text_fr'],
          color: '#003f7f',
          accent: '#fff',
          jerseyUrl: options[1][language === 'en' ? 'option_image_en' : 'option_image_fr'],
        },
      ];
    }
    return [];
  }, [questionType, options, language]);

  // Mock player data for player questions
  const mockPlayers = useMemo(() => {
    if (questionType === 'player' && options.length >= 2) {
      return options.map((opt, index) => ({
        id: `player-${index + 1}`,
        name: opt[language === 'en' ? 'option_text_en' : 'option_text_fr'],
        imageUrl: opt[language === 'en' ? 'option_image_en' : 'option_image_fr'],
        team: {
          id: `team-${index + 1}`,
          name: `Team ${index + 1}`,
          color: index === 0 ? '#c8102e' : '#003f7f',
          accent: '#fff',
        },
      }));
    }
    return [];
  }, [questionType, options, language]);

  const renderQuestionPreview = () => {
    // Since we can't directly import the UI components yet (vendor sync needs to run),
    // we'll create a simple preview representation
    return (
      <div className="bg-[#1a1f2e] rounded-xl p-4">
        {/* Header with Points Badge */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-[#00ff88] rounded-full flex items-center justify-center">
              <span className="text-black text-xs font-bold">#</span>
            </div>
            <span className="text-gray-400 text-sm">{totalPoints}pts</span>
          </div>
        </div>

        {/* Question Text */}
        <div className="text-white font-medium text-center mb-4">{questionText}</div>

        {/* Question Image if present */}
        {questionImage && (
          <div className="mb-4">
            <img
              src={questionImage}
              alt="Question"
              className="w-full rounded-lg object-cover max-h-48"
            />
          </div>
        )}

        {/* Options based on question type */}
        <div className="space-y-3">
          {questionType === 'team' && mockTeams.length === 2 && (
            <div className="grid grid-cols-2 gap-4">
              {mockTeams.map(team => (
                <div
                  key={team.id}
                  className="bg-[#252b3b] rounded-xl p-6 border-2 border-transparent hover:border-white/20 transition-all cursor-pointer"
                >
                  <div className="flex flex-col items-center space-y-3">
                    <img
                      src={team.jerseyUrl || '/no-jersey.png'}
                      alt={team.name}
                      className="w-16 h-16"
                    />
                    <div className="font-bold text-lg text-white">{team.name}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {questionType === 'player' && mockPlayers.length > 0 && (
            <div className="grid grid-cols-2 gap-4">
              {mockPlayers.map(player => (
                <div
                  key={player.id}
                  className="bg-[#252b3b] rounded-xl p-6 border-2 border-transparent hover:border-white/20 transition-all cursor-pointer"
                >
                  <div className="flex flex-col items-center space-y-3">
                    <img
                      src={player.imageUrl || '/no-jersey.png'}
                      alt={player.name}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                    <div className="text-center">
                      <div className="font-bold text-white">{player.name}</div>
                      <div className="text-sm text-gray-400">{player.team.name}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {questionType === 'prop' && options.length >= 2 && (
            <div className="grid grid-cols-2 gap-4">
              <button className="bg-[#252b3b] rounded-xl p-6 border-2 border-transparent hover:border-white/20 transition-all">
                <div className="flex flex-col items-center space-y-2">
                  {options[0][language === 'en' ? 'option_image_en' : 'option_image_fr'] ? (
                    <img
                      src={options[0][language === 'en' ? 'option_image_en' : 'option_image_fr']}
                      alt={
                        options[0][language === 'en' ? 'option_text_en' : 'option_text_fr'] || 'Yes'
                      }
                      className="w-16 h-16 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                      <span className="text-2xl">👍</span>
                    </div>
                  )}
                  <span className="font-bold text-white">
                    {options[0][language === 'en' ? 'option_text_en' : 'option_text_fr'] || 'Yes'}
                  </span>
                </div>
              </button>
              <button className="bg-[#252b3b] rounded-xl p-6 border-2 border-transparent hover:border-white/20 transition-all">
                <div className="flex flex-col items-center space-y-2">
                  {options[1][language === 'en' ? 'option_image_en' : 'option_image_fr'] ? (
                    <img
                      src={options[1][language === 'en' ? 'option_image_en' : 'option_image_fr']}
                      alt={
                        options[1][language === 'en' ? 'option_text_en' : 'option_text_fr'] || 'No'
                      }
                      className="w-16 h-16 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                      <span className="text-2xl">👎</span>
                    </div>
                  )}
                  <span className="font-bold text-white">
                    {options[1][language === 'en' ? 'option_text_en' : 'option_text_fr'] || 'No'}
                  </span>
                </div>
              </button>
            </div>
          )}

          {questionType === 'over_under' && (
            <div className="space-y-3">
              {options.map((option, index) => (
                <button
                  key={index}
                  className="w-full bg-[#252b3b] rounded-xl p-4 border-2 border-transparent hover:border-white/20 transition-all"
                >
                  <div className="flex items-center gap-3">
                    {option[language === 'en' ? 'option_image_en' : 'option_image_fr'] && (
                      <img
                        src={option[language === 'en' ? 'option_image_en' : 'option_image_fr']}
                        alt={option[language === 'en' ? 'option_text_en' : 'option_text_fr']}
                        className="w-12 h-12 rounded object-cover"
                      />
                    )}
                    <span className="font-bold text-white">
                      {option[language === 'en' ? 'option_text_en' : 'option_text_fr']}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}

          {questionType === 'tiebreaker' && (
            <div className="bg-[#252b3b] rounded-xl p-4">
              <input
                type="number"
                placeholder="Enter your answer"
                className="w-full bg-[#1a1f2e] text-white rounded-lg px-4 py-3 border border-gray-600 focus:border-[#00ff88] focus:outline-none"
                disabled
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            Question Preview
            <Badge variant="outline">{questionType}</Badge>
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[600px]">
          <div className="p-4 bg-gray-100 dark:bg-gray-900 rounded-lg">
            {renderQuestionPreview()}
          </div>
        </ScrollArea>

        <div className="text-sm text-muted-foreground text-center mt-2">
          This preview shows how the question will appear to users
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PreviewModal;
